import type { PartnerEnum } from '../groveSchedulerClient'
import { getPartnerBaseUrl } from './partnerEnvironment'
import type { FormFieldValue, PartnerSchemaInternal } from './types'

/**
 * Validates clinic URL format
 * Ensures the value is a valid http/https URL
 */
const validateClinicUrl = (
  value: FormFieldValue,
): { valid: boolean; error?: string } => {
  if (typeof value !== 'string') {
    return { valid: false, error: 'Clinic URL must be a text value' }
  }

  const trimmedValue = value.trim()

  if (!trimmedValue) {
    return { valid: false, error: 'Clinic URL is required' }
  }

  try {
    const url = new URL(trimmedValue)

    // Ensure protocol is http or https
    if (!['http:', 'https:'].includes(url.protocol)) {
      return {
        valid: false,
        error: 'Clinic URL must use http:// or https:// protocol',
      }
    }

    return { valid: true }
  } catch {
    return {
      valid: false,
      error: 'Invalid URL format. Example: https://clinic.janeapp.com',
    }
  }
}

/**
 * Build OAuth authorization URL for Jane
 * Uses the environment-appropriate base URL (staging / prod-uk / prod-us).
 *
 * @param agentId - The agent ID to authorize
 * @returns Complete OAuth authorization URL
 */
export const buildJaneOAuthUrl = (agentId: string): string => {
  const base = getPartnerBaseUrl()
  return `${base}/jane/authorize?agent_id=${encodeURIComponent(agentId)}`
}

/**
 * Jane partner configuration schema
 * Requires clinic_url. Customer Admin will complete OAuth in web-app.
 */
export const janeSchema: PartnerSchemaInternal = {
  partner: 'jane' as PartnerEnum,
  displayName: 'Jane',
  requiresOAuth: true,
  fields: [
    {
      key: 'clinic_url',
      label: 'Clinic URL',
      type: 'text',
      required: true,
      placeholder: 'https://clinic.janeapp.com',
      helperText: 'The base URL your office uses to log in to the Jane app',
      validation: validateClinicUrl,
    },
  ],
  buildOAuthUrl: buildJaneOAuthUrl,
  postCreationInfo:
    'After creating this agent, the customer will need to authorize with their Jane credentials. They can do this from their web portal, or you can share the authorization link with them from the agent details page.',
  authHelp:
    'If authorization fails, verify the Clinic URL matches the URL used to log in to Jane. Authorization requires Jane credentials for the specified clinic.',
}
